@if (shouldShowBlock) {
  <div class="chat-v2-reasoning-container" [class.has-body]="hasBodyContent">
    <!-- Header: Metallic Shimmer or Static Done Badge with Chevron -->
    <div
      class="chat-v2-reasoning-header-btn"
      [class.is-static]="!canExpand"
      [attr.tabindex]="canExpand ? 0 : -1"
      [attr.role]="canExpand ? 'button' : null"
      [attr.aria-expanded]="canExpand ? isExpanded : null"
      (click)="toggleExpand()"
      (keydown.enter)="toggleExpand()"
    >
      @if (showShimmer) {
        <span class="chat-v2-reasoning-title chat-v2-metallic-shimmer">
          {{ headerText }}
        </span>
      } @else {
        <span class="chat-v2-reasoning-title">
          {{ headerText }}
        </span>
      }

      @if (canExpand) {
        <i
          class="fas chat-v2-chevron text-xs"
          [ngClass]="isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"
          aria-hidden="true"
        ></i>
      }
    </div>

    <!-- Collapsed Mode: Single Active Event Preview with 900ms Throttling -->
    @if (showCollapsedEvent && collapsedStep; as activeStep) {
      <div class="chat-v2-collapsed-wrapper">
        @for (item of [activeStep]; track safeDisplayIndex) {
          <div
            class="chat-v2-collapsed-row"
            [attr.data-step-index]="safeDisplayIndex"
          >
            <div class="chat-v2-timeline-item">
              <div class="chat-v2-timeline-gutter">
                <div class="chat-v2-dot-wrapper">
                  <div
                    class="chat-v2-status-dot"
                    [ngClass]="getStatusDotClass(activeStep.status)"
                    [attr.aria-label]="activeStep.status || 'running'"
                  ></div>
                </div>
              </div>

              <div class="chat-v2-item-body is-last">
                <div
                  class="chat-v2-item-header"
                  [class.is-static]="!hasStepDetails(activeStep)"
                  [attr.tabindex]="hasStepDetails(activeStep) ? 0 : -1"
                  [attr.role]="hasStepDetails(activeStep) ? 'button' : null"
                  (click)="
                    hasStepDetails(activeStep) &&
                      toggleStepDetails(safeDisplayIndex)
                  "
                  (keydown.enter)="
                    hasStepDetails(activeStep) &&
                      toggleStepDetails(safeDisplayIndex)
                  "
                >
                  <span
                    class="chat-v2-item-title"
                    [class.chat-v2-metallic-shimmer]="
                      activeStep.status === 'running' ||
                      activeStep.status === 'active'
                    "
                    >{{ getStepTitle(activeStep) }}</span
                  >
                  @if (hasStepDetails(activeStep)) {
                    <i
                      class="fas fa-chevron-right chat-v2-chevron text-xs transition-transform duration-200"
                      [class.rotate-90]="isStepDetailsOpen(safeDisplayIndex)"
                      aria-hidden="true"
                    ></i>
                  }
                </div>

                @if (
                  getStepSubtitle(activeStep) &&
                  !isStepDetailsOpen(safeDisplayIndex)
                ) {
                  <p class="chat-v2-item-subtitle line-clamp-2">
                    {{ getStepSubtitle(activeStep) }}
                  </p>
                }

                @if (isStepDetailsOpen(safeDisplayIndex)) {
                  <div class="chat-v2-details-drawer">
                    @if (getStepSubtitle(activeStep)) {
                      <p class="chat-v2-item-subtitle mb-2">
                        {{ getStepSubtitle(activeStep) }}
                      </p>
                    }

                    @if (activeStep.capabilityName) {
                      <div class="chat-v2-capability-badge">
                        {{ config.capabilityPrefix
                        }}{{ activeStep.capabilityName }}
                      </div>
                    }

                    @if (extractStepJsonData(activeStep); as jsonData) {
                      <div class="chat-v2-json-container">
                        <rapider-json-viewer
                          [data]="jsonData"
                        ></rapider-json-viewer>
                      </div>
                    } @else if (activeStep.data) {
                      <div class="chat-v2-json-container">
                        <pre class="chat-v2-raw-json">{{
                          activeStep.data | json
                        }}</pre>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- Expanded Mode: 1px Connected Vertical Timeline with Status Dots -->
    @if (showExpandedTimeline) {
      <div class="chat-v2-timeline-wrapper">
        @for (
          step of steps;
          track trackByStep($index, step);
          let isLast = $last;
          let idx = $index
        ) {
          <div class="chat-v2-timeline-item">
            <div class="chat-v2-timeline-gutter">
              <div class="chat-v2-dot-wrapper">
                <div
                  class="chat-v2-status-dot"
                  [ngClass]="getStatusDotClass(step.status)"
                  [attr.aria-label]="step.status || 'idle'"
                ></div>
              </div>
              @if (!isLast) {
                <div class="chat-v2-timeline-stroke"></div>
              }
            </div>

            <div class="chat-v2-item-body" [class.is-last]="isLast">
              <div
                class="chat-v2-item-header"
                [class.is-static]="!hasStepDetails(step)"
                [attr.tabindex]="hasStepDetails(step) ? 0 : -1"
                [attr.role]="hasStepDetails(step) ? 'button' : null"
                (click)="hasStepDetails(step) && toggleStepDetails(idx)"
                (keydown.enter)="hasStepDetails(step) && toggleStepDetails(idx)"
              >
                <span
                  class="chat-v2-item-title"
                  [class.chat-v2-metallic-shimmer]="
                    (step.status === 'running' || step.status === 'active') &&
                    isStreaming
                  "
                  >{{ getStepTitle(step) }}</span
                >
                @if (hasStepDetails(step)) {
                  <i
                    class="fas fa-chevron-right chat-v2-chevron text-xs transition-transform duration-200"
                    [class.rotate-90]="isStepDetailsOpen(idx)"
                    aria-hidden="true"
                  ></i>
                }
              </div>

              @if (getStepSubtitle(step) && !isStepDetailsOpen(idx)) {
                <p class="chat-v2-item-subtitle line-clamp-2">
                  {{ getStepSubtitle(step) }}
                </p>
              }

              @if (isStepDetailsOpen(idx)) {
                <div class="chat-v2-details-drawer">
                  @if (getStepSubtitle(step)) {
                    <p class="chat-v2-item-subtitle mb-2">
                      {{ getStepSubtitle(step) }}
                    </p>
                  }

                  @if (step.capabilityName) {
                    <div class="chat-v2-capability-badge">
                      {{ config.capabilityPrefix }}{{ step.capabilityName }}
                    </div>
                  }

                  @if (extractStepJsonData(step); as jsonData) {
                    <div class="chat-v2-json-container">
                      <rapider-json-viewer
                        [data]="jsonData"
                      ></rapider-json-viewer>
                    </div>
                  } @else if (step.data) {
                    <div class="chat-v2-json-container">
                      <pre class="chat-v2-raw-json">{{ step.data | json }}</pre>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>
}
